/*
 * FALLBACK ONLY: reached solely from the legacy branch of firstPass, when no
 * aligner/WhisperX helper answers (phones). Kept working per project
 * constraint — do not extend.
 */

/**
 * The legacy first pass: Gemini invents the timestamps, everything after it
 * repairs them.
 *
 * This is the ORIGINAL pipeline and it is still the one every phone runs,
 * because a phone cannot host the MMS aligner or WhisperX. It was lifted out of
 * `firstPass.ts` verbatim; the only edits were the ones the move forced
 * (`options.x` became `ctx.x`, and one uniform 2-space dedent).
 *
 * THE SHAPE
 * ---------
 *   draw -> refine -> SCORE -> re-roll a bad draw   (up to MAX_DRAWS)
 *     -> burst-first grouping (word-level cuts are a fiction inside a burst)
 *     -> slice + persist        <- everything from here on is committed
 *     -> clip QA, best effort   <- polish only; a failure never fails the run
 *
 * See ./README.md for the module-level flow map.
 *
 * WHAT THIS IS *NOT* RESPONSIBLE FOR
 * ----------------------------------
 * Deciding whether to run. `firstPass.runFirstPass` owns that: it tries the
 * aligned path first and falls through to here, and the rules for when that
 * fall-through is allowed (an explicitly chosen engine never degrades silently)
 * live there, not here.
 *
 * It also does NOT own the failure path. Throwing is correct: the caller's
 * outer catch walks the task's status back and writes the error.
 */

import {
  getSegmentsByTask,
  getTask,
  newId,
  replaceSegmentsForTask,
  updateTask,
} from '../../db';
import { round3 } from '../../lib/num';
import type { RawSegment, Segment } from '../../types';
import { extractMono, sliceSegmentsToWav } from '../audioSlicer';
import { transcribeFirstPass } from '../gemini';
import { detectOnsets } from '../onsetDetect';
import {
  endRun,
  note,
  patchRun,
  snapshotStage,
  type CompletenessSnapshot,
  type DrawLog,
  type RunLog,
} from '../pipelineLog';
import { PHASE_START, report, throwIfAborted, type ProgressCallback } from '../progress';
import {
  buildScoringContext,
  drawTimingFault,
  GOOD_ALIGNMENT_SCORE,
  scoreAlignmentWith,
  type AlignmentScore,
} from './alignmentScore';
import {
  groupByBursts,
  MAX_GROUP_SEC,
  MAX_GROUP_WORDS,
  MIN_GROUP_SEC,
  ONSET_SPLIT_STRENGTH,
} from './burstGroups';
import { QA_FAILED_CONFIDENCE, runClipQa, type ClipQaStats } from './clipQa';
import type { CompletenessReport } from './gapFill';
import { refineBoundaries } from './refineBoundaries';

/**
 * `ClipQaStats` reaches `firstPass.RunFirstPassResult` through here on purpose.
 *
 * This module is the SINGLE façade of the quarantine: exactly one import line
 * anywhere outside `services/legacy/` may name a file in it, and that line is
 * in `firstPass.ts`. Routing the stats type through the same line keeps that
 * true without pushing a legacy shape up into a core module.
 */
export type { ClipQaStats } from './clipQa';

/**
 * How many times the first pass may be drawn before we accept the best one.
 *
 * gemini-2.5-flash is measurably nondeterministic on this task even at
 * `temperature: 0` — repeated identical requests on the same 44.8 s file have
 * produced both usable timings and wholesale mis-placements. Since the app can
 * now *measure* which one it got (see `alignmentScore`), a bad draw should cost
 * one extra API call rather than the user's afternoon.
 */
const MAX_DRAWS = 2;

/** One first-pass draw, refined and scored, competing to be kept. */
interface Candidate {
  attempt: number;
  /** The model's own timings, before refinement. */
  raw: RawSegment[];
  rawScore: AlignmentScore;
  /** After the global speech-time warp alone. Diagnostic. */
  warpOnly?: RawSegment[];
  /** After DTW + snap, before onset anchoring. Only set when stage 5 moved something. */
  beforeOnsets?: RawSegment[];
  /** Final refined timings — what would be sliced. */
  segments: RawSegment[];
  score: AlignmentScore;
  /** Set when the draw failed the duration sanity check. */
  fault: string | null;
}

/** Sub-spans of the `qa` phase, so the bar keeps moving through all five. */
const QA_SPANS: Record<
  'recognising' | 'resegmenting' | 'gapfilling' | 'repadding' | 'merging',
  [number, number]
> = {
  recognising: [0.66, 0.84],
  resegmenting: [0.84, 0.9],
  gapfilling: [0.9, 0.95],
  repadding: [0.95, 0.98],
  merging: [0.98, 0.99],
};

const QA_LABELS: Record<keyof typeof QA_SPANS, string> = {
  recognising: 'Double-checking clips…',
  resegmenting: 'Re-cutting run-together speech…',
  gapfilling: 'Listening for missed words…',
  repadding: 'Re-cutting clips Gemini missed…',
  merging: 'Trying merges…',
};

/**
 * The knobs only this pipeline reads.
 *
 * They used to be declared inline on `RunFirstPassOptions`, where nothing said
 * that choosing the aligned engine makes all four inert. `RunFirstPassOptions`
 * now extends this interface, so the public surface is unchanged and the docs
 * sit next to the code that honours them.
 */
export interface LegacyPipelineOptions {
  /** Skip the clip-QA stage (saves one Gemini call per clip). Default false. */
  skipQa?: boolean;
  /** Max concurrent clip-QA requests. Default 3. */
  qaConcurrency?: number;
  /** Wall-clock ceiling on the whole QA stage, ms. Default 4 min. 0 = none. */
  qaBudgetMs?: number;
  /**
   * Emit one segment per WORD instead of per burst-group. Default false.
   *
   * An escape hatch for measurement, not a supported mode: word-level cuts of
   * connected speech are the failure burst-first grouping exists to remove (see
   * services/legacy/burstGroups.ts), and the isolation-QA pass rate under them was 19%.
   */
  skipGrouping?: boolean;
}

/** Everything the run already established, handed over explicitly. */
export interface LegacyPipelineContext extends LegacyPipelineOptions {
  taskId: string;
  /** The decoded original — the SAME buffer the upload was encoded from. */
  buffer: AudioBuffer;
  /** 16 kHz mono WAV of the whole recording. */
  uploadBlob: Blob;
  durationSec: number;
  /** The open run log; this pipeline appends its draws to `run.draws`. */
  run: RunLog;
  signal?: AbortSignal;
  onProgress?: ProgressCallback;
  /** Seconds of padding added to each side of a clip. */
  padSec?: number;
  /** Extra prompt context, e.g. "the speaker is a child, language is Arabic". */
  extraInstructions?: string;
}

export interface LegacyPipelineResult {
  taskId: string;
  segmentCount: number;
  qa?: ClipQaStats;
}

/**
 * Run the legacy pipeline to completion: it persists the segments, writes the
 * task back to `review`, closes the run log and reports `done`.
 */
export async function runLegacyPipeline(ctx: LegacyPipelineContext): Promise<LegacyPipelineResult> {
  const { taskId, buffer, uploadBlob, durationSec, run, signal, onProgress } = ctx;

  /* -- transcribe, scoring each draw ------------------------------ */
  /*
   * Gemini's word order is reliable; its timestamps are not, and how wrong
   * they are varies run to run on identical input. So: draw, re-anchor,
   * SCORE the result against the audio itself, and re-roll a bad draw.
   *
   * `refineBoundaries` projects the model's timeline onto the audio's own
   * energy envelope and returns it untouched when it cannot improve on it —
   * that bail is the single most useful thing in the log, because a bailed
   * refinement leaves raw model timings in the output and nothing else in the
   * pipeline says so.
   */
  const scoring = buildScoringContext(buffer);
  note(
    `audio: ${scoring.bursts.length} speech bursts, ${scoring.totalSpeechSec.toFixed(2)}s of speech in ${durationSec.toFixed(2)}s`,
  );

  let best: Candidate | null = null;
  let bestClean: Candidate | null = null;

  for (let attempt = 1; attempt <= MAX_DRAWS; attempt++) {
    report(onProgress, {
      phase: 'transcribing',
      message: attempt === 1 ? 'Listening & transcribing…' : `Re-listening (draw ${attempt})…`,
      ratio: PHASE_START.transcribing,
    });

    const drawn: RawSegment[] = await transcribeFirstPass({
      audio: uploadBlob,
      durationSec,
      mimeType: 'audio/wav',
      signal,
      extraInstructions: ctx.extraInstructions,
      attempt,
    });
    throwIfAborted(signal);

    const log: DrawLog = { attempt, rawSegmentCount: drawn.length, chosen: false };
    run.draws.push(log);

    if (drawn.length === 0) {
      log.timingFault = 'empty response';
      if (attempt < MAX_DRAWS) {
        patchRun({ retryReason: 'first draw was empty' });
        continue;
      }
      break;
    }

    // Cheap, text-free sanity check on the time axis before spending any
    // effort refining it. A draw that runs past the end of the file, or stops
    // well short, invented its own timeline.
    const fault = drawTimingFault(drawn, durationSec);
    if (fault) log.timingFault = fault;

    report(onProgress, {
      phase: 'transcribing',
      message: 'Aligning timings to the audio…',
      ratio: (PHASE_START.transcribing + PHASE_START.slicing) / 2,
    });

    log.rawScore = scoreAlignmentWith(scoring, drawn);
    const refined = refineBoundaries(buffer, drawn);
    const refinedScore = scoreAlignmentWith(scoring, refined.segments);
    log.refinedScore = refinedScore;
    log.refine = {
      applied: refined.applied,
      reason: refined.reason,
      meanShiftSec: round3(refined.meanShiftSec),
      maxShiftSec: round3(refined.maxShiftSec),
      coverageBefore: round3(refined.coverageBefore),
      coverageAfter: round3(refined.coverageAfter),
    };
    if (!refined.applied) {
      note(`draw ${attempt}: refineBoundaries BAILED (${refined.reason}) — raw model timings kept`);
    }
    if (refined.warpOnly) {
      log.refine.warpOnlyScore = scoreAlignmentWith(scoring, refined.warpOnly).score;
      log.refine.dtwApplied = refined.dtwApplied;
    }
    if (refined.onsets) {
      const o = refined.onsets;
      log.refine.onsets = {
        // `beforeOnsets` is only set when the stage changed something, so the
        // "before" score falls back to the final one when it did not.
        scoreBefore: refined.beforeOnsets
          ? scoreAlignmentWith(scoring, refined.beforeOnsets).score
          : refinedScore.score,
        scoreAfter: refinedScore.score,
        burstsEligible: o.burstsEligible,
        burstsApplied: o.burstsApplied,
        segmentsMoved: o.segmentsMoved,
        meanShiftSec: o.meanShiftSec,
        maxShiftSec: o.maxShiftSec,
        bursts: o.bursts,
      };
    }

    const candidate: Candidate = {
      attempt,
      raw: drawn,
      rawScore: log.rawScore,
      warpOnly: refined.warpOnly,
      beforeOnsets: refined.beforeOnsets,
      segments: refined.segments,
      score: refinedScore,
      fault,
    };
    if (!best || refinedScore.score > best.score.score) best = candidate;
    if (!fault && (!bestClean || refinedScore.score > bestClean.score.score)) bestClean = candidate;

    const good = !fault && refinedScore.score >= GOOD_ALIGNMENT_SCORE;
    if (good || attempt === MAX_DRAWS) break;

    const why = fault
      ? `draw ${attempt} rejected: ${fault}`
      : `draw ${attempt} scored ${refinedScore.score.toFixed(2)} < ${GOOD_ALIGNMENT_SCORE} ` +
        `(span ${pctOf(refinedScore.spanOnSpeech)}, recall ${pctOf(refinedScore.speechRecall)}, ` +
        `${refinedScore.implausibleCount} impossible durations, ${refinedScore.emptyBursts} empty bursts)`;
    patchRun({ retryReason: why });
    note(`${why} — re-rolling`);
  }

  // Prefer the best draw that passed the timing sanity check; fall back to the
  // best of the faulty ones rather than failing the run outright.
  const winner = bestClean ?? best;
  if (!winner) {
    throw new Error('Gemini heard nothing in this audio. Try a louder or longer recording.');
  }
  const chosenLog = run.draws.find((d) => d.attempt === winner.attempt);
  if (chosenLog) chosenLog.chosen = true;
  patchRun({ chosenAttempt: winner.attempt });
  if (run.draws.length > 1) {
    note(
      `kept draw ${winner.attempt} of ${run.draws.length} · scores ${run.draws
        .map((d) => `${d.attempt}:${d.refinedScore ? d.refinedScore.score.toFixed(2) : 'n/a'}`)
        .join(' ')}`,
    );
  }
  if (winner.score.score < GOOD_ALIGNMENT_SCORE) {
    note(
      `WARNING: best available alignment score ${winner.score.score.toFixed(2)} is below ${GOOD_ALIGNMENT_SCORE}; ` +
        'clips are likely to be mis-cut inside multi-word bursts',
    );
  }

  let raw: RawSegment[] = winner.segments;
  // Four snapshots, so a reader can attribute every boundary move: what the
  // model said, what the global warp did to it, what the DTW + snap did after,
  // and what the onset stage did INSIDE the bursts — which is the only stage
  // that can move a boundary with no silence under it.
  snapshotStage('gemini-raw', winner.raw, {
    score: winner.rawScore,
    note: `draw ${winner.attempt}${winner.fault ? ` · timing fault: ${winner.fault}` : ''}`,
  });
  if (winner.warpOnly) snapshotStage('after-global-warp', winner.warpOnly);
  if (winner.beforeOnsets) {
    const onsetLog = run.draws.find((d) => d.attempt === winner.attempt)?.refine?.onsets;
    snapshotStage('after-dtw-snap', winner.beforeOnsets, {
      score: scoreAlignmentWith(scoring, winner.beforeOnsets),
    });
    snapshotStage('onsets', raw, {
      score: winner.score,
      note: onsetLog
        ? `${onsetLog.burstsApplied}/${onsetLog.burstsEligible} multi-segment bursts re-cut, ${onsetLog.segmentsMoved} boundaries moved`
        : undefined,
    });
  } else {
    snapshotStage('after-dtw-snap', raw, { score: winner.score });
    snapshotStage('onsets', raw, { note: 'onset anchoring changed nothing' });
  }
  throwIfAborted(signal);

  /* -- burst-first grouping --------------------------------------- */
  /*
   * Everything above produced ONE SEGMENT PER WORD, and inside a burst of
   * connected speech that is a fiction: the model's intra-burst timestamps are
   * generated rather than measured, and even a perfectly placed word-level cut
   * of "انا اسمي امجد" yields 0.2 s clips that neither the model nor a human
   * can recognise on their own (measured isolation-QA pass rate under 0.25 s:
   * 0 of 30).
   *
   * So this stage throws the intra-burst boundaries away and re-cuts on the
   * evidence the recording actually contains — silence between bursts, and
   * confident spectral onsets inside them — fusing the words it cannot
   * separate into one multi-word label. It runs AFTER the snapshots above so
   * the log still shows what each alignment stage did to the word timings;
   * what changes here is only how those words are grouped into rows.
   *
   * Word-to-BURST assignment is inherited untouched. Grouping cannot fix a
   * word that landed in the wrong burst — that is a first-pass recall problem
   * and belongs to gap fill.
   */
  let groupingApplied = false;
  if (!ctx.skipGrouping) {
    try {
      const mono = extractMono(buffer, 0, durationSec);
      const { onsets } = detectOnsets(mono, buffer.sampleRate);
      const grouped = groupByBursts(raw, {
        env: scoring.env,
        bursts: scoring.bursts,
        onsets,
        durationSec,
      });
      if (grouped.segments.length > 0) {
        patchRun({
          grouping: {
            segmentsBefore: raw.length,
            segmentsAfter: grouped.segments.length,
            maxGroupWords: MAX_GROUP_WORDS,
            maxGroupSec: MAX_GROUP_SEC,
            minGroupSec: MIN_GROUP_SEC,
            splitStrength: ONSET_SPLIT_STRENGTH,
            multiWordGroups: grouped.multiWordGroups,
            wordsFused: grouped.wordsFused,
            emptyBursts: grouped.emptyBursts,
            confidentSplits: grouped.confidentSplits,
            forcedSplits: grouped.forcedSplits,
            capViolations: grouped.capViolations,
            widenedToFloor: grouped.widenedToFloor,
            boundaryEvidenceFrac: round3(grouped.boundaryEvidenceFrac),
            bursts: grouped.bursts,
          },
        });
        note(
          `grouping: ${raw.length} words -> ${grouped.segments.length} segments · ` +
            `${grouped.multiWordGroups} multi-word groups (${grouped.wordsFused} words fused) · ` +
            `${grouped.confidentSplits} splits on confident onsets, ${grouped.forcedSplits} forced by a cap · ` +
            `${grouped.emptyBursts} burst(s) with no labels at all`,
        );
        if (grouped.forcedSplits > 0 || grouped.capViolations > 0) {
          note(
            `grouping caps: ${grouped.forcedSplits} boundary(ies) invented to honour ` +
              `${MAX_GROUP_WORDS} words / ${MAX_GROUP_SEC}s, ${grouped.capViolations} group(s) still over a cap`,
          );
        }
        raw = grouped.segments;
        groupingApplied = true;
        snapshotStage('burst-groups', raw, {
          score: scoreAlignmentWith(scoring, raw),
          note: `${grouped.segments.length} groups · boundaries on silence or a confident onset: ${Math.round(grouped.boundaryEvidenceFrac * 100)}%`,
        });
      } else {
        note('grouping produced nothing — keeping the word-level segmentation');
      }
    } catch (err) {
      // Grouping is a re-cut of segments that are already coherent; a failure
      // here must leave the word-level ones standing rather than fail the run.
      console.warn('[lableforge] burst grouping failed, keeping word-level segments:', err);
      note(`burst grouping threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  }
  throwIfAborted(signal);

  /* -- slice ----------------------------------------------------- */
  report(onProgress, {
    phase: 'slicing',
    message: `Cutting ${raw.length} clips…`,
    ratio: PHASE_START.slicing,
    done: 0,
    total: raw.length,
  });

  const segments: Segment[] = raw.map((r, index) => ({
    id: newId(),
    taskId,
    index,
    startSec: r.startSec,
    endSec: r.endSec,
    heard: r.heard,
    contextual: r.contextual,
    confidence: r.confidence,
    kind: r.kind,
    verify: 'pending',
  }));

  const span = PHASE_START.saving - PHASE_START.slicing;
  const clips = await sliceSegmentsToWav(buffer, raw, {
    padSec: ctx.padSec,
    onProgress: (done, total) => {
      report(onProgress, {
        phase: 'slicing',
        message: `Cutting clips… ${done}/${total}`,
        ratio: PHASE_START.slicing + (total ? (done / total) * span : 0),
        done,
        total,
      });
    },
  });
  throwIfAborted(signal);

  /* -- persist --------------------------------------------------- */
  report(onProgress, { phase: 'saving', message: 'Saving clips…', ratio: PHASE_START.saving });

  // The user can delete a task while its first pass is in flight. Writing the
  // segments anyway would leave orphans in the Verify queue that belong to no
  // task and can never be reconciled away.
  if (!(await getTask(taskId))) throw new Error('That task was deleted while it was processing.');

  // One transaction: drops whatever a previous attempt left behind (segments
  // and clips — the original audio must survive a retry) and writes the new
  // set, so the task is never briefly segment-less or clip-less.
  await replaceSegmentsForTask(
    taskId,
    segments.map((s, i) => ({ segment: s, clip: clips[i] })),
  );

  /* -- clip QA (best effort) -------------------------------------- */
  /*
   * Everything above is already committed. From here on the job is polish:
   * re-listen to each clip on its own, widen or merge the ones Gemini can no
   * longer recognise, and flag whatever is left. `runClipQa` swallows its own
   * API errors, and this try/catch covers the rest — a QA problem must never
   * turn a finished first pass into a failed one.
   */
  let qa: ClipQaStats | undefined;
  if (!ctx.skipQa) {
    try {
      report(onProgress, {
        phase: 'qa',
        message: `Double-checking ${segments.length} clips…`,
        ratio: PHASE_START.qa,
        done: 0,
        total: segments.length,
      });
      qa = await runClipQa(buffer, segments, {
        signal,
        padSec: ctx.padSec,
        concurrency: ctx.qaConcurrency,
        budgetMs: ctx.qaBudgetMs,
        structuralBoundaries: groupingApplied,
        onProgress: ({ phase, done, total }) => {
          const [from, to] = QA_SPANS[phase];
          report(onProgress, {
            phase: 'qa',
            message: `${QA_LABELS[phase]} ${done}/${total}`,
            ratio: from + (total ? (done / total) * (to - from) : 0),
            done,
            total,
          });
        },
      });
    } catch (err) {
      // Logged, not thrown: the segments and clips on disk are still good.
      console.warn('[lableforge] clip QA failed, keeping the first pass as-is:', err);
      note(`clip QA threw: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  /* -- record what QA and re-segmentation actually did -------------- */
  /*
   * Read the segments back rather than reusing the in-memory list: QA merges
   * rows and re-segmentation inserts and drops them, so the DB is the only
   * honest account of what the user will be shown.
   */
  try {
    const finalSegments = (await getSegmentsByTask(taskId)).sort((a, b) => a.index - b.index);
    if (qa?.budgetExceeded) {
      note(
        `qa-skipped-budget: QA stopped after ${(qa.elapsedMs / 1000).toFixed(0)}s; ` +
          `${qa.skipped} segments were never re-listened to and keep their original confidence`,
      );
    }
    if (qa) {
      const { resegment, gapFill, ...rest } = qa;
      patchRun({
        qa: rest as unknown as Record<string, unknown>,
        resegment: resegment as unknown as Record<string, unknown> | undefined,
        gapFill: gapFill as unknown as Record<string, unknown> | undefined,
      });
      /*
       * LABEL COMPLETENESS — the headline pair.
       *
       * Recorded separately from the QA blob because it is the one number that
       * corresponds to the complaint this stage was built for: not "are the
       * boundaries on speech" (span/recall already say that) but "is every
       * word that was spoken on the list". A run whose alignment score is 0.86
       * and whose gap count is 6 is a run that will be heard as drifting, and
       * before this field there was nowhere to see that.
       */
      if (gapFill?.before) {
        patchRun({
          completeness: {
            before: toCompleteness(gapFill.before),
            after: gapFill.after ? toCompleteness(gapFill.after) : undefined,
          },
        });
        const b = gapFill.before;
        const a = gapFill.after ?? b;
        note(
          `label completeness: ${pctOf(b.speechCoveredFrac)} -> ${pctOf(a.speechCoveredFrac)} of speech covered · ` +
            `${b.unlabelledSpeechSec.toFixed(2)}s -> ${a.unlabelledSpeechSec.toFixed(2)}s unlabelled · ` +
            `${b.gapCount} -> ${a.gapCount} gaps >= 180ms · ` +
            `${gapFill.segmentsInserted} word(s) inserted from ${gapFill.regionsAttempted} gap clip(s)`,
        );
        if (a.gapCount > 0) {
          note(
            `remaining gaps: ${a.gaps
              .map((g) => `${g.startSec.toFixed(2)}-${g.endSec.toFixed(2)}`)
              .join(' ')}`,
          );
        }
      }
    }
    patchRun({
      qaVerdicts: finalSegments.map((s) => ({
        index: s.index,
        heard: s.heard,
        confidence: s.confidence,
        flagged: s.confidence <= QA_FAILED_CONFIDENCE,
      })),
    });
    const finalScore = scoreAlignmentWith(scoring, finalSegments);
    snapshotStage('after-qa-and-reseg', finalSegments, { score: finalScore });
    const flagged = finalSegments.filter((s) => s.confidence <= QA_FAILED_CONFIDENCE).length;
    if (flagged > finalSegments.length * 0.5) {
      note(
        `WARNING: ${flagged}/${finalSegments.length} clips could not be recognised on their own after QA — ` +
          'the boundaries inside multi-word bursts are probably still wrong',
      );
    }
  } catch (err) {
    note(`could not snapshot the final segments: ${err instanceof Error ? err.message : String(err)}`);
  }

  // QA is the only stage that can change how many segments a task has: merges
  // remove rows and burst re-segmentation both adds and drops them, so the
  // count has to come from QA itself rather than be derived here.
  const finalCount = qa?.finalCount ?? segments.length;

  await updateTask(taskId, { status: 'review', error: undefined });
  await endRun('ok');
  report(onProgress, {
    phase: 'done',
    message:
      qa && qa.flaggedLowConfidence > 0
        ? `${finalCount} segments ready to verify · ${qa.flaggedLowConfidence} flagged`
        : `${finalCount} segments ready to verify`,
    ratio: 1,
    done: finalCount,
    total: finalCount,
  });

  return { taskId, segmentCount: finalCount, qa };
}

function pctOf(n: number): string {
  return `${Math.round(n * 100)}%`;
}

/** The log-shaped subset of a {@link CompletenessReport} (the gap list is big). */
function toCompleteness(r: CompletenessReport): CompletenessSnapshot {
  return {
    speechCoveredFrac: r.speechCoveredFrac,
    unlabelledSpeechSec: r.unlabelledSpeechSec,
    totalSpeechSec: r.totalSpeechSec,
    gapCount: r.gapCount,
    gapSec: r.gapSec,
    segmentCount: r.segmentCount,
  };
}
